import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateBreadcrumbSchema } from '@/utils/seo';

interface DMVForm {
  number: string;
  name: string;
  desc: string;
  url: string;
  langs?: string[];
}

interface SubCategory {
  title: string;
  forms: DMVForm[];
  note?: string;
}

interface Category {
  id: string;
  title: string;
  icon: string;
  anchor: string;
  subcategories: SubCategory[];
}

const BASE = 'https://dmv.nv.gov/pdfforms/';

const CATEGORIES: Category[] = [
  {
    id: 'driver-license',
    title: 'Driver License Forms',
    icon: 'ri-id-card-line',
    anchor: 'dl',
    subcategories: [
      {
        title: 'Applications',
        note: 'Visit Commercial Driver Licensing for CDL forms.',
        forms: [
          { number: 'DMV 002', name: 'Application for Driving Privileges or ID Card', desc: 'Do not mail this form! Mailed applications are rejected — please see DMV 204 for mail-in applications.', url: `${BASE}dmv002en.pdf`, langs: ['Spanish', 'Tagalog'] },
          { number: 'DMV 204', name: 'Application for Driving Privilege or ID Card By Mail', desc: 'For drivers currently licensed in Nevada who meet eligibility requirements to complete transactions by mail. Cannot be used for original issuance.', url: `${BASE}dmv204en.pdf`, langs: ['Spanish', 'Tagalog'] },
          { number: 'DMV 22', name: 'Change of Address Notification by Mail', desc: 'Change your address online! See Address Changes for the online option.', url: `${BASE}dmv22en.pdf`, langs: ['Spanish', 'Tagalog'] },
          { number: 'DP 38', name: 'Minor Affidavit', desc: 'Certifies that a minor is aware of various requirements and laws. Also used by a parent/guardian to cancel a minor driver license or instruction permit.', url: `${BASE}dp38.pdf` },
          { number: 'DMV 301', name: 'Certification of Attendance', desc: 'For drivers under 18 to show proof of school attendance.', url: `${BASE}dmv301.pdf` },
          { number: 'DMV 302', name: 'Parent/Guardian Consent for Minors', desc: 'Drivers under 18 will need parent/guardian consent for instruction permit/licensing privilege.', url: `${BASE}dmv302.pdf` },
          { number: 'DLD 130', name: 'Beginning Driver Experience Log', desc: 'For certification of Behind-the-Wheel Driving Experience for drivers under 18.', url: `${BASE}dld130.pdf` },
          { number: 'DLD 121', name: 'Driver Certification of Eligibility — Farm/Military', desc: 'To claim exemption from federal commercial licensing regulations for farm/military drivers.', url: `${BASE}dld121.pdf` },
          { number: 'DP 046', name: 'Driver Certification of Eligibility — Firefighters', desc: 'To claim exemption from federal commercial licensing regulations for firefighters.', url: `${BASE}dp046.pdf` },
          { number: 'DMV 128', name: 'Declaration of Homeless Status', desc: 'For the homeless to apply for a one-time waiver for a duplicate license or ID card.', url: `${BASE}dmv128.pdf` },
          { number: 'DP 190', name: 'Request for Waiver of Duplicate Fees — Released Prisoners', desc: 'For recently-released prisoners to apply for a one-time waiver for a duplicate license or ID card.', url: `${BASE}dp190.pdf` },
          { number: 'DMV 013', name: 'Released Prisoner Driver\'s License and ID Card Information', desc: 'Proof of identity and other requirements for prisoners recently released from a Nevada institution.', url: `${BASE}dmv013.pdf` },
          { number: 'DMV 007', name: 'Application for Alternate Address', desc: 'For applicants authorized by law to list an alternate address on their license. These circumstances are very rare.', url: `${BASE}dmv007.pdf` },
          { number: 'DMV 015', name: 'Drive Test Waiver for Foreign Nationals', desc: 'For licensed drivers from Taiwan or South Korea moving to Nevada under reciprocity agreement.', url: `${BASE}dmv015.pdf` },
          { number: 'SP 59', name: 'Application for Expedited Service Permit', desc: 'For disabled individuals seeking expedited service.', url: `${BASE}sp59.pdf` },
          { number: 'MC 097', name: 'Replacement IFTA License/Decal Form', desc: 'For those licensed under the Nevada International Fuel Tax Agreement (NRS 485.265).', url: `${BASE}mc097.pdf` },
          { number: 'MC 098', name: 'Longer Combination Vehicle (LCV) Permit Application', desc: 'For Reducible Vehicle/Load Combinations Exceeding 70 Feet in Length.', url: `${BASE}mc098.pdf` },
          { number: 'SP 98', name: 'Standard and Personalized Electric Vehicle License Plates Form', desc: 'For standard and personalized license plates for passenger vehicles, light commercial vehicles and motorcycles wholly powered by an electric motor.', url: `${BASE}sp98.pdf` },
        ],
      },
      {
        title: 'Medical',
        forms: [
          { number: 'DLD 7', name: 'Confidential Physicians Report', desc: 'For medical assessments of a motorist\'s driving ability.', url: `${BASE}dld7.pdf` },
          { number: 'DP 18', name: 'Eye Examination Certificate', desc: 'For visual examinations completed by a physician.', url: `${BASE}dp18.pdf` },
          { number: 'DLD 98', name: 'Application For Approval To Drive With Bioptic Lenses', desc: 'For licensed vision specialists who dispense bioptic devices.', url: `${BASE}dld98.pdf` },
          { number: 'DLD 23', name: 'Agency Request for Re-Examination', desc: 'For law enforcement, government agencies and other institutions to request a motorist undergo driving tests.', url: `${BASE}dld23.pdf` },
          { number: 'DLD 23a', name: 'Request for Re-Evaluation', desc: 'For relatives to request a motorist undergo driving tests.', url: `${BASE}dld23a.pdf` },
        ],
      },
      {
        title: 'Restricted Licenses, Suspensions and Revocations',
        forms: [
          { number: 'DMV 21', name: 'Application for Restricted License', desc: 'To apply for a standard restricted license during a license revocation, for medical hardship, school transportation or child visitation.', url: `${BASE}dmv21.pdf` },
          { number: 'DMV 247', name: '24/7 Restricted License Application', desc: 'To apply for a restricted license for those ordered to participate in the 24/7 Sobriety and Drug Monitoring Program.', url: `${BASE}dmv247.pdf` },
          { number: 'DLD 33', name: 'One Year Default Affidavit', desc: 'For drivers whose license is suspended under financial responsibility laws.', url: `${BASE}dld33.pdf` },
          { number: 'DLD 93', name: 'Two Year Default Affidavit', desc: 'For drivers whose license is suspended under financial responsibility laws.', url: `${BASE}dld93.pdf` },
          { number: 'DLD 95', name: 'Statement of Surrender of Nevada Driver License and Vehicle Registration', desc: 'For drivers whose license is suspended under financial responsibility laws.', url: `${BASE}dld95.pdf` },
          { number: 'DP 5', name: 'Voluntary License Identification Card Surrender', desc: 'For drivers surrendering their license for insurance, medical or other reasons. Note: reacquiring a license requires all testing requirements.', url: `${BASE}dp5.pdf` },
        ],
      },
      {
        title: 'Residency',
        note: 'See Proof of Identity and Residency for the documents you need to apply for an original license or ID.',
        forms: [
          { number: 'DMV 005', name: 'Certification of Nevada Residency', desc: 'To enable another person to provide proof of an applicant\'s residency.', url: `${BASE}dmv005.pdf` },
          { number: 'DMV 115', name: 'Relief Agency or Shelter Certification', desc: 'To enable social service agencies to provide proof of the applicant\'s residency.', url: `${BASE}dmv115.pdf` },
          { number: 'DMV 116', name: 'Property Owner Residency Affidavit', desc: 'To enable a property owner to provide proof of the applicant\'s residency.', url: `${BASE}dmv116.pdf` },
        ],
      },
    ],
  },
  {
    id: 'vehicle-registration',
    title: 'Vehicle Registration Forms',
    icon: 'ri-car-line',
    anchor: 'Registration',
    subcategories: [
      {
        title: 'Vehicle Registration',
        forms: [
          { number: 'DMV 22', name: 'Change of Address Notification by Mail', desc: 'Change your address online! See Address Changes for the online option.', url: `${BASE}dmv22en.pdf`, langs: ['Spanish', 'Tagalog'] },
          { number: 'VP 013', name: 'Application for Duplicate Registration Certificate', desc: 'There is a $5 fee for each certificate requested. You may mail this application and a check, money order or Payment Authorization to the address on the form.', url: `${BASE}vp013.pdf` },
          { number: 'MC 32', name: 'Change of Address Application', desc: 'Needed to renew or change a license or registration. Must be signed by the owner.', url: `${BASE}mc032.pdf` },
          { number: 'VP 222', name: 'Application for Vehicle Registration', desc: 'Needed when applying for an original registration. Must be signed by the owner.', url: `${BASE}vp222.pdf` },
          { number: 'VP 018', name: 'Affidavit of Non-Operation', desc: 'Certifies that a specific vehicle has not been operated on Nevada public highways.', url: `${BASE}vp018.pdf` },
          { number: 'VP 015', name: 'Vehicle Inspection Certificate (VIN Verification)', desc: 'Must be completed by a DMV representative, a peace officer or other authorized agent.', url: `${BASE}vp015.pdf` },
          { number: 'VP 30', name: 'Moped Registration Application/Inspection', desc: 'Do not mail this form. The inspection must be completed by a DMV representative, Sheriff or Deputy Sheriff.', url: `${BASE}vp30.pdf` },
          { number: 'EC 008', name: 'Emission Control Exemption Application', desc: 'For Nevada residents temporarily out of state who are unable to obtain a smog check.', url: `${BASE}ec008.pdf` },
          { number: 'EC 018', name: 'Odometer Certification for Emission Exemption', desc: 'To claim the emission inspection exemption for vehicles registered as a Classic Rod or Classic Vehicle.', url: `${BASE}ec018.pdf` },
          { number: 'VP 198', name: 'Affidavit of Non-Residency', desc: 'To register vehicles which were purchased in Nevada, taken out-of-state and then returned to Nevada.', url: `${BASE}vp198.pdf` },
          { number: 'SP 75', name: 'License Plate Hearing Request', desc: 'To request an Administrative Hearing for a denied or recalled personalized license plate number.', url: `${BASE}sp75.pdf` },
          { number: 'VP 193', name: 'Registration Service Transmittal Form', desc: 'All document preparation services are required to be registered with the Nevada Secretary of State.', url: `${BASE}vp193.pdf` },
          { number: 'VP 247', name: 'Registration Service Transaction Request', desc: 'All document preparation services are required to be registered with the Nevada Secretary of State.', url: `${BASE}vp247.pdf` },
          { number: 'VP 264', name: 'Application for Non-Resident Business Permit', desc: 'For commercial vehicles under 10,001 pounds. See Movement Permits — Commercial for details.', url: `${BASE}vp264.pdf` },
          { number: 'VP 017', name: 'Military Surplus Vehicle Inspection', desc: 'For vehicles labeled for "OFF-ROAD USE ONLY" — cannot be converted for on-road use with the exception of an off highway two-wheeled motorcycle.', url: `${BASE}VP-017.pdf` },
        ],
      },
      {
        title: 'Fees and Tax Exemptions',
        note: 'See Nevada County Assessors to apply for tax exemptions for veterans, surviving spouses and the blind.',
        forms: [
          { number: 'VP 210', name: 'Registration Fee Refund Request Form', desc: 'To request a refund under certain limited circumstances. See Plate Surrender for details.', url: `${BASE}vp210.pdf` },
          { number: 'VP 187', name: 'Designation of Use for Veterans Exemption', desc: 'Used to designate that a portion of a Veterans Exemption be directed to the Nevada Veterans Home Account.', url: `${BASE}vp187.pdf` },
          { number: 'VP 259', name: 'Affidavit for Transfer of Exemption to Spouse', desc: 'Used to transfer a Veterans Exemption to the veteran\'s spouse.', url: `${BASE}vp259.pdf` },
          { number: 'VP 242', name: 'Trust Affidavit for Exemptions', desc: 'To enable the use of a Veterans or other exemption when the vehicle is titled to a trust.', url: `${BASE}vp242.pdf` },
          { number: 'VP 258', name: 'Active Duty Military Combat Affidavit', desc: 'To claim an exemption from registration late fees.', url: `${BASE}vp258.pdf` },
          { number: 'VP 203', name: 'Non-Resident, Active Duty Military GST Exemption Affidavit', desc: 'For active duty military members who are residents of another state and stationed in Nevada.', url: `${BASE}vp203.pdf` },
          { number: 'VP 203s', name: 'Non-Resident Military Spouse GST Exemption Affidavit', desc: 'For spouses of active duty military members who are residents of another state and stationed in Nevada.', url: `${BASE}vp203s.pdf` },
          { number: 'VP 154', name: 'Application for Governmental Services Tax Exemption', desc: 'For Nevada Native American Tribal Members residing on tribal lands.', url: `${BASE}vp154.pdf` },
          { number: 'VP 159', name: 'Farmer/Rancher Affidavit', desc: 'Used to adjust registration fees on large farm vehicles.', url: `${BASE}vp159.pdf` },
        ],
      },
      {
        title: 'License Plates',
        note: 'See License Plates for all plate-related forms.',
        forms: [
          { number: 'VP 202', name: 'Lost, Stolen or Mutilated License Plate Affidavit', desc: 'You must obtain new plates in person at a DMV Full Service Office.', url: `${BASE}vp202.pdf` },
          { number: 'SP 14', name: 'Duplicate License Plate Application', desc: 'Plates to replace damaged or unreadable plates in the same style. Not for stolen plates.', url: `${BASE}sp14.pdf` },
          { number: 'SP 27', name: 'Application for Disabled Persons License Plates/Placards', desc: 'Application for disabled persons license plates and/or parking placards.', url: `${BASE}sp27.pdf` },
        ],
      },
      {
        title: 'Insurance',
        note: 'See Insurance for details. Most insurance issues can be handled online or by phone without a DMV office visit.',
        forms: [
          { number: 'NVL 010', name: 'Vehicle Registration Insurance Suspension Guide', desc: 'Items to check if the DMV is reporting a lapse in your insurance coverage.', url: `${BASE}nvl010.pdf` },
          { number: 'NVL 019', name: 'Declaration of Responsibility', desc: 'To declare that you will meet financial responsibility requirements.', url: `${BASE}nvl019.pdf` },
          { number: 'NVL 003', name: 'Dormant Vehicle Affidavit', desc: 'Used to reinstate a vehicle registration suspended for failure to maintain liability insurance. Only used if the vehicle has not been operated during the lapse.', url: `${BASE}nvl003.pdf` },
          { number: 'NVL 025', name: 'Property Owner Affidavit', desc: 'To declare that a vehicle was stored at a specific address.', url: `${BASE}nvl025.pdf` },
          { number: 'NVL 005', name: 'Insurance Hearing Request', desc: 'To request an Administrative Hearing for an insurance-related vehicle registration suspension.', url: `${BASE}nvl005.pdf` },
        ],
      },
    ],
  },
  {
    id: 'vehicle-ownership',
    title: 'Vehicle Ownership Forms',
    icon: 'ri-file-text-line',
    anchor: 'Ownership',
    subcategories: [
      {
        title: 'Vehicle Ownership',
        forms: [
          { number: 'VP 104', name: 'Bill Of Sale', desc: 'Releases a private party\'s ownership interest in a vehicle.', url: `${BASE}vp104.pdf` },
          { number: 'VP 012', name: 'Application for Duplicate Nevada Certificate of Title', desc: 'For use only if the vehicle was last titled in Nevada. If titled in another state, you must obtain a duplicate from that state.', url: `${BASE}vp012.pdf` },
          { number: 'VP 186', name: 'Lien Release', desc: 'Releases an ownership interest held by a financial institution or other third party.', url: `${BASE}vp186.pdf` },
          { number: 'VP 268', name: 'Electronic Lienholder Reassignment Affidavit', desc: 'Assigns lienholder rights on electronic titles.', url: `${BASE}vp268.pdf` },
          { number: 'VP 212', name: 'Attempt to Contact', desc: 'Instructions for attempting to contact the owner of a vehicle.', url: `${BASE}vp212.pdf` },
          { number: 'VP 206', name: 'Lost Title Affidavit', desc: 'Certifies that a recently-issued title was not received.', url: `${BASE}vp206.pdf` },
          { number: 'VP 136', name: 'Power of Attorney', desc: 'Used to convey the authority to transfer the ownership of or register a vehicle.', url: `${BASE}vp136.pdf` },
          { number: 'VP 249', name: 'Affidavit of Acknowledgment for a Power of Attorney', desc: 'To certify a true copy of a Power of Attorney.', url: `${BASE}vp249.pdf` },
          { number: 'VP 019', name: 'Erasure Affidavit', desc: 'Used to state reasons for changes and errors in completing the buyer or reassignment sections of vehicle titles.', url: `${BASE}vp019.pdf` },
          { number: 'VP 185', name: 'One and the Same Affidavit', desc: 'Used to clarify the identity of a vehicle owner.', url: `${BASE}vp185.pdf` },
        ],
      },
      {
        title: 'Estates and Trusts',
        forms: [
          { number: 'VP 024', name: 'Affidavit for Transfer of Title for Estates Without Probate', desc: 'For a deceased person\'s heirs, estate or creditors to claim ownership of a vehicle.', url: `${BASE}vp024.pdf` },
          { number: 'VP 188', name: 'Trustee Appointment and Powers Affidavit', desc: 'To declare a person has trustee powers to transfer ownership of a vehicle.', url: `${BASE}vp188.pdf` },
          { number: 'VP 239', name: 'Transfer on Death Application', desc: 'To add, change or delete a beneficiary designated to receive a vehicle upon the death of the owner.', url: `${BASE}vp239.pdf` },
          { number: 'VP 241', name: 'Transfer on Death — Beneficiary\'s Affidavit for Title', desc: 'For a beneficiary to claim ownership of a vehicle when the original title is not available.', url: `${BASE}vp241.pdf` },
          { number: 'VP 238', name: 'Moped / Motorcycle Declaration', desc: 'To certify that a vehicle is a motorcycle after a determination that it does not meet the specifications of a moped.', url: `${BASE}vp238.pdf` },
        ],
      },
      {
        title: 'Publications',
        forms: [
          { number: '—', name: 'Registration and Title Guide', desc: 'Instructions for auto dealers and other licensees. Refer to the Salvage Title Guide for salvage vehicle titling.', url: `${BASE}regtitle.pdf` },
          { number: '—', name: 'Wrecker and Tow Car Guide', desc: 'Guide for wrecker and tow car operators.', url: `${BASE}wreckertow.pdf` },
          { number: '—', name: 'Salvage Title and Non-Repairable Vehicle Certificate Guide', desc: 'Guide for salvage title and non-repairable vehicle certificate processes.', url: `${BASE}salvageguide.pdf` },
          { number: '—', name: 'Off-Highway Vehicle Registration and Title Guide', desc: 'Guide for OHV registration and title processes.', url: `${BASE}ohvregtitle.pdf` },
          { number: '—', name: 'Motor Vehicle Equipment Requirements', desc: 'Equipment requirements for motor vehicles, trailers, and motorcycles.', url: `${BASE}equipreqmv.pdf` },
        ],
      },
      {
        title: 'Salvage, Rebuilt, Constructed and Non-Repairable Vehicles',
        forms: [
          { number: 'VP 213', name: 'Application for Salvage Title or Non-Repairable Vehicle Certificate', desc: 'To issue these types of titles or to apply for a duplicate salvage title.', url: `${BASE}vp213.pdf` },
          { number: 'VP 260', name: 'Application for Non-Repairable Vehicle Certificate', desc: 'For use only by a licensed automobile wrecker, dismantler, or recycler.', url: `${BASE}vp260.pdf` },
          { number: 'VP 236', name: 'Insurance Company Affidavit Without Endorsed Title', desc: 'To issue a title when vehicle owner(s) cannot be contacted.', url: `${BASE}vp236.pdf` },
          { number: 'VP 275', name: 'Donation Affidavit without Endorsed Title', desc: 'For non-profit organizations when vehicle owner(s) cannot be contacted.', url: `${BASE}vp275.pdf` },
          { number: 'VP 209', name: 'Authorization for Vehicle Restoration', desc: 'To request DMV authorization to restore a vehicle.', url: `${BASE}vp209.pdf` },
          { number: 'VP 064', name: 'Certificate of Inspection/Affidavit of Construction', desc: 'To certify the safety of rebuilt, reconstructed or assembled vehicles.', url: `${BASE}vp064.pdf` },
          { number: 'VP 064A', name: 'Certificate of Inspection for Rebuilt Vehicles (Not Salvage)', desc: 'To certify the safety of vehicles that have had certain repairs.', url: `${BASE}vp064a.pdf` },
          { number: 'VP 064M', name: 'Certificate of Inspection/Affidavit of Construction — Motorcycles', desc: 'To certify the safety of rebuilt, reconstructed or assembled motorcycles. Use VP 254 for conversions.', url: `${BASE}vp064m.pdf` },
          { number: 'VP 254', name: 'Motorcycle Highway Use Affidavit', desc: 'To certify the safety of an off-road motorcycle that has been converted to on-road use.', url: `${BASE}vp254.pdf` },
          { number: 'VP 223', name: 'Certificate of Inspection/Affidavit of Construction — Trailers', desc: 'To certify the safety of rebuilt, reconstructed or assembled trailers.', url: `${BASE}vp223.pdf` },
          { number: 'VP 279', name: 'Abandoned Vehicle Safety Affidavit', desc: 'To be used when submitting a lien sale packet for an abandoned vehicle and bypass the salvage title requirements.', url: `${BASE}vp279.pdf` },
        ],
      },
      {
        title: 'Repossessions and Liens',
        forms: [
          { number: 'VP 020', name: 'Repossession Affidavit', desc: 'Affidavit for vehicle repossession transactions.', url: `${BASE}vp020.pdf` },
          { number: 'VP 263', name: 'Lien Sale Requirements', desc: 'Requirements and procedures for lien sale transactions.', url: `${BASE}vp263.pdf` },
          { number: 'VP 147', name: 'Lien Sale Affidavit', desc: 'Affidavit required for lien sale transactions.', url: `${BASE}vp147.pdf` },
          { number: 'VP 201', name: 'Lien Sale Registration Certification', desc: 'Certification for lien sale registration.', url: `${BASE}vp201.pdf` },
          { number: 'VP 162', name: 'Letter of Transmittal for Abandoned Junk Vehicles', desc: 'Letter of transmittal for abandoned junk vehicle processing.', url: `${BASE}vp162.pdf` },
          { number: 'VP 262', name: 'Abandoned Vehicle Lien Transfer', desc: 'For abandoned vehicle lien transfer transactions.', url: `${BASE}vp262.pdf` },
          { number: 'VP 267', name: 'Abandoned Recreational Vehicle Title Affidavit', desc: 'Affidavit for abandoned recreational vehicle title processing.', url: `${BASE}vp267.pdf` },
          { number: 'VP 277', name: 'Contest Lien Sale Validity', desc: 'To notify DMV that a vehicle lien is being contested in a Justice Court.', url: `${BASE}vp277.pdf` },
        ],
      },
    ],
  },
  {
    id: 'miscellaneous',
    title: 'Miscellaneous Forms',
    icon: 'ri-file-list-3-line',
    anchor: 'Misc',
    subcategories: [
      {
        title: 'Complaints',
        note: 'The DMV Compliance Enforcement Division (CED) accepts complaints against auto dealers, lessors, emissions stations, driving schools and other businesses regulated by the DMV.',
        forms: [
          { number: 'SP 74', name: 'Personalized License Plate Complaint Form', desc: 'See Nevada Administrative Code 482.230 for applicable regulations.', url: `${BASE}sp74.pdf` },
          { number: 'MC 083', name: 'Fuel Dealer Complaint Form', desc: 'For consumers who wish to file a complaint against a retail station dispensing LPG or CNG.', url: `${BASE}mc083.pdf` },
        ],
      },
      {
        title: 'Crashes',
        forms: [
          { number: 'SR 1', name: 'Report of Traffic Crash', desc: 'You must file this form within 10 days of any accident which was: (1) not investigated at the scene by law enforcement and (2) caused injury, death or more than $750 damage.', url: `${BASE}sr1.pdf` },
          { number: 'DLD 68', name: 'Failure to Report Crash Affidavit', desc: 'Affidavit for failure to report a traffic crash.', url: `${BASE}dld68.pdf` },
        ],
      },
      {
        title: 'Window Tinting',
        forms: [
          { number: 'DO 200', name: 'Application for Window Tint Exemption', desc: 'For ambulance, hearse, law enforcement or medical exemption and those who qualify to exceed the maximum window tinting standards.', url: `${BASE}do200.pdf` },
        ],
      },
    ],
  },
];

const PAYMENT_FORM: DMVForm = {
  number: 'ADM 205',
  name: 'Payment Authorization Form',
  desc: 'Authorizes the DMV to charge your credit or debit card for license or registration fees.',
  url: `${BASE}adm205.pdf`,
};

export default function DMVFormsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'Nevada DMV Forms & Publications - Complete Forms Directory | Speedy DMV',
    description: 'Complete directory of all Nevada DMV forms organized by service category. Driver license, vehicle registration, ownership, and miscellaneous forms with direct PDF downloads. Las Vegas DMV services.',
    keywords: 'Nevada DMV forms, DMV forms Las Vegas, Nevada vehicle registration forms, Nevada title transfer forms, DMV PDF forms Nevada',
    canonical: `${siteUrl}/nevada-dmv-forms`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        generateBreadcrumbSchema([{ name: 'Home', url: '/' }, { name: 'Nevada DMV Forms', url: '/nevada-dmv-forms' }])
      ]
    }
  });

  const [openSubs, setOpenSubs] = useState<Record<string, boolean>>({
    'driver-license-0': true,
    'vehicle-registration-0': true,
    'vehicle-ownership-0': true,
    'miscellaneous-0': true,
  });
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const toggleSub = (key: string) => {
    setOpenSubs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const expandAll = () => {
    const all: Record<string, boolean> = {};
    CATEGORIES.forEach(cat => cat.subcategories.forEach((_, i) => { all[`${cat.id}-${i}`] = true; }));
    setOpenSubs(all);
  };

  const collapseAll = () => setOpenSubs({});

  const matchesSearch = (f: DMVForm) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return f.number.toLowerCase().includes(q) || f.name.toLowerCase().includes(q) || f.desc.toLowerCase().includes(q);
  };

  const visibleCategories = CATEGORIES.filter(cat =>
    activeCategory === 'all' || cat.id === activeCategory
  ).map(cat => ({
    ...cat,
    subcategories: cat.subcategories.map(sub => ({
      ...sub,
      forms: sub.forms.filter(matchesSearch),
    })).filter(sub => sub.forms.length > 0),
  })).filter(cat => cat.subcategories.length > 0);

  const totalForms = CATEGORIES.reduce((acc, cat) => acc + cat.subcategories.reduce((a, s) => a + s.forms.length, 0), 0);

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />

      {/* ── HERO ── */}
      <section className="pt-[104px] bg-[#003366]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center space-x-2 text-white/50 text-xs mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#F5A623]">Nevada DMV Forms</span>
          </div>
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                Nevada DMV<br /><span className="text-[#F5A623]">Forms &amp; Publications</span>
              </h1>
              <p className="text-white/75 text-base mb-5 leading-relaxed">
                Complete directory of all Nevada DMV forms organized by service category — identical to the official dmv.nv.gov forms page. All forms link directly to official PDF downloads.
              </p>
              <div className="bg-[#FFF8E7] border border-[#F5A623] rounded p-4 mb-5">
                <p className="text-[#7a5c00] text-xs leading-relaxed">
                  <strong>Important:</strong> Many forms require signatures from a Notary Public or a DMV representative to be valid. These may be filled out in advance, but you must wait to sign and date them until you are actually at the Notary or DMV Office. All forms are PDF format — <a href="https://get.adobe.com/reader/" target="_blank" rel="noopener noreferrer nofollow" className="underline font-semibold">Download Adobe Reader</a> to view them.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <a href="https://dmv.nv.gov/dmvforms.htm" target="_blank" rel="noopener noreferrer nofollow"
                  className="flex items-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-5 py-2.5 rounded font-bold text-sm transition-colors whitespace-nowrap">
                  <i className="ri-external-link-line"></i>
                  <span>Official DMV Forms Page</span>
                </a>
                <Link to="/appointment"
                  className="flex items-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-5 py-2.5 rounded font-bold text-sm transition-colors whitespace-nowrap">
                  <i className="ri-calendar-check-line"></i>
                  <span>We Handle All Forms For You</span>
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: `${totalForms}+`, label: 'Official Forms Listed', icon: 'ri-file-list-3-line' },
                { value: '4', label: 'Service Categories', icon: 'ri-layout-grid-line' },
                { value: 'PDF', label: 'All Forms Format', icon: 'ri-file-pdf-line' },
                { value: 'Free', label: 'Download All Forms', icon: 'ri-download-line' },
              ].map((s, i) => (
                <div key={i} className="bg-white/10 border border-white/20 rounded p-4 text-center">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#F5A623] rounded mx-auto mb-2">
                    <i className={`${s.icon} text-white text-base`}></i>
                  </div>
                  <p className="text-[#F5A623] text-xl font-bold">{s.value}</p>
                  <p className="text-white/60 text-xs mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Category nav tabs */}
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex overflow-x-auto">
              {[{ id: 'all', label: 'All Categories', icon: 'ri-layout-grid-line' }, ...CATEGORIES.map(c => ({ id: c.id, label: c.title, icon: c.icon }))].map(tab => (
                <button key={tab.id} onClick={() => setActiveCategory(tab.id)}
                  className={`flex items-center space-x-2 px-5 py-4 text-sm font-semibold whitespace-nowrap border-b-2 transition-all cursor-pointer flex-shrink-0 ${
                    activeCategory === tab.id
                      ? 'border-[#F5A623] text-[#F5A623] bg-white/5'
                      : 'border-transparent text-white/60 hover:text-white hover:border-white/30'
                  }`}>
                  <i className={tab.icon}></i>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── DISCLAIMER ── */}
      <div className="bg-[#FFF8E7] border-b-2 border-[#F5A623]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-start space-x-3">
            <i className="ri-information-line text-[#F5A623] text-lg flex-shrink-0 mt-0.5"></i>
            <p className="text-sm text-[#7a5c00] leading-relaxed">
              <strong>Educational Reference Only.</strong> This page lists Nevada DMV forms for informational purposes. Information may not be current. By using this site you agree that Speedy DMV Services is not responsible for outdated information. We are a <strong>licensed 3rd party provider — NOT the Nevada DMV.</strong> Always verify current forms at <a href="https://dmv.nv.gov/dmvforms.htm" target="_blank" rel="noopener noreferrer nofollow" className="underline font-semibold">dmv.nv.gov</a>.
            </p>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* Payment form + search + expand controls */}
        <div className="flex flex-col lg:flex-row gap-5 mb-8">
          {/* Payment Authorization quick link */}
          <a href={PAYMENT_FORM.url} target="_blank" rel="noopener noreferrer nofollow"
            className="flex items-center space-x-3 bg-[#003366] text-white px-5 py-3 rounded hover:bg-[#002244] transition-colors group flex-shrink-0">
            <div className="w-9 h-9 flex items-center justify-center bg-[#F5A623] rounded flex-shrink-0">
              <i className="ri-bank-card-line text-white text-base"></i>
            </div>
            <div>
              <p className="font-bold text-sm">{PAYMENT_FORM.name} ({PAYMENT_FORM.number})</p>
              <p className="text-white/60 text-xs">{PAYMENT_FORM.desc}</p>
            </div>
            <i className="ri-download-line text-[#F5A623] text-lg ml-2 flex-shrink-0"></i>
          </a>

          {/* Search */}
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder="Search forms by number, name, or description..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded text-sm focus:outline-none focus:border-[#003366] text-gray-800 bg-white"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-base"></i>
              </button>
            )}
          </div>

          {/* Expand/Collapse */}
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={expandAll} className="flex items-center space-x-1.5 px-4 py-2.5 border border-[#003366] text-[#003366] hover:bg-blue-50 rounded text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-expand-up-down-line"></i>
              <span>Expand All</span>
            </button>
            <button onClick={collapseAll} className="flex items-center space-x-1.5 px-4 py-2.5 border border-gray-200 text-gray-500 hover:bg-gray-50 rounded text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-collapse-diagonal-line"></i>
              <span>Collapse All</span>
            </button>
          </div>
        </div>

        {/* We Handle It Banner */}
        <div className="bg-[#003366] rounded p-5 mb-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 flex items-center justify-center bg-[#F5A623] rounded flex-shrink-0">
              <i className="ri-shield-check-line text-white text-xl"></i>
            </div>
            <div>
              <p className="text-white font-bold text-base">Don't Want to Deal With Forms?</p>
              <p className="text-white/65 text-sm">Speedy DMV Services handles all paperwork for you. We know exactly which forms you need and complete them correctly the first time.</p>
            </div>
          </div>
          <div className="flex gap-3 flex-shrink-0">
            <a href="tel:7029797202" className="flex items-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-5 py-2.5 rounded font-bold text-sm transition-colors whitespace-nowrap">
              <i className="fas fa-phone text-xs"></i>
              <span>702.979.7202</span>
            </a>
            <Link to="/appointment" className="flex items-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-5 py-2.5 rounded font-bold text-sm transition-colors whitespace-nowrap">
              <i className="ri-calendar-check-line"></i>
              <span>Book Appointment</span>
            </Link>
          </div>
        </div>

        {/* Forms by category */}
        {visibleCategories.length === 0 ? (
          <div className="text-center py-16">
            <i className="ri-search-line text-gray-300 text-5xl mb-4 block"></i>
            <p className="text-gray-500 font-semibold">No forms found for &ldquo;{search}&rdquo;</p>
            <button onClick={() => setSearch('')} className="mt-3 text-[#003366] text-sm underline cursor-pointer">Clear search</button>
          </div>
        ) : (
          <div className="space-y-12">
            {visibleCategories.map(cat => (
              <section key={cat.id} id={cat.anchor}>
                {/* Category header */}
                <div className="flex items-center space-x-3 mb-1 pb-3 border-b-2 border-[#F5A623]">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#003366] rounded flex-shrink-0">
                    <i className={`${cat.icon} text-[#F5A623] text-lg`}></i>
                  </div>
                  <h2 className="text-2xl font-bold text-[#003366]">{cat.title}</h2>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full ml-auto flex-shrink-0">
                    {cat.subcategories.reduce((a, s) => a + s.forms.length, 0)} forms
                  </span>
                </div>

                {/* Subcategory accordions */}
                <div className="space-y-2 mt-4">
                  {cat.subcategories.map((sub, si) => {
                    const key = `${cat.id}-${si}`;
                    const isOpen = openSubs[key] !== false && (openSubs[key] === true || si === 0 || search !== '');
                    return (
                      <div key={si} className="border border-gray-200 rounded overflow-hidden">
                        {/* Subcategory toggle */}
                        <button
                          onClick={() => toggleSub(key)}
                          className="w-full flex items-center justify-between px-5 py-3.5 bg-white hover:bg-gray-50 transition-colors cursor-pointer text-left"
                        >
                          <div className="flex items-center space-x-3">
                            <span className="font-bold text-[#003366] text-sm">{sub.title}</span>
                            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{sub.forms.length}</span>
                          </div>
                          <i className={`${isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[#F5A623] text-xl flex-shrink-0`}></i>
                        </button>

                        {isOpen && (
                          <div className="border-t border-gray-100 bg-gray-50 px-5 py-4">
                            {sub.note && (
                              <div className="flex items-start space-x-2 mb-4 bg-[#FFF8E7] border border-[#F5A623]/30 rounded p-3">
                                <i className="ri-information-line text-[#F5A623] flex-shrink-0 mt-0.5"></i>
                                <p className="text-xs text-[#7a5c00] leading-relaxed">{sub.note}</p>
                              </div>
                            )}
                            <div className="space-y-2">
                              {sub.forms.map((form, fi) => (
                                <div key={fi} className="bg-white border border-gray-200 rounded hover:border-[#003366] transition-colors group">
                                  <div className="flex items-start p-4 gap-4">
                                    {/* Form number badge */}
                                    {form.number !== '—' && (
                                      <span className="inline-block bg-[#003366] text-[#F5A623] text-xs font-bold px-2.5 py-1 rounded font-mono whitespace-nowrap flex-shrink-0 mt-0.5">
                                        {form.number}
                                      </span>
                                    )}
                                    {/* Form info */}
                                    <div className="flex-1 min-w-0">
                                      <p className="font-semibold text-[#003366] text-sm leading-snug mb-1 group-hover:text-[#F5A623] transition-colors">
                                        {form.name}
                                      </p>
                                      <p className="text-gray-500 text-xs leading-relaxed">{form.desc}</p>
                                      {form.langs && (
                                        <div className="flex items-center space-x-2 mt-1.5">
                                          <span className="text-xs text-gray-400">Also available in:</span>
                                          {form.langs.map(lang => (
                                            <span key={lang} className="text-xs text-[#003366] font-semibold bg-blue-50 px-1.5 py-0.5 rounded">{lang}</span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    {/* Download link */}
                                    <a
                                      href={form.url}
                                      target="_blank"
                                      rel="noopener noreferrer nofollow"
                                      className="flex items-center space-x-1.5 bg-[#003366] hover:bg-[#F5A623] text-white px-3 py-2 rounded text-xs font-bold transition-colors whitespace-nowrap flex-shrink-0"
                                      title={`Download ${form.name}`}
                                    >
                                      <i className="ri-file-pdf-line text-sm"></i>
                                      <span>PDF</span>
                                    </a>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-14 bg-[#003366] rounded p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-2">
            Need Help Completing <span className="text-[#F5A623]">Nevada DMV Forms?</span>
          </h2>
          <p className="text-white/70 text-sm mb-6 max-w-xl mx-auto">
            Our licensed team handles all DMV paperwork for you — correctly, the first time. No confusion, no rejected forms, no wasted trips.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-8 py-3.5 rounded font-bold text-sm transition-colors whitespace-nowrap">
              <i className="fas fa-phone text-xs"></i>
              <span>Call 702.979.7202</span>
            </a>
            <Link to="/appointment" className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-8 py-3.5 rounded font-bold text-sm transition-colors whitespace-nowrap">
              <i className="ri-calendar-check-line"></i>
              <span>Book Appointment</span>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
